import { createArticle } from "../lib/api/articles/createArticle.ts";
import { IArticle, ICreateArticle, IUpdateArticle } from "../models/article.ts";
import { ILazyAttachment } from "../models/zip_content.ts";
import {
  ImportProgressStatus,
  ImportType,
} from "../modules/parts/ImportProgressModal.tsx";
import { updateArticle } from "../lib/api/articles/updateArticle.ts";
import { handleAttachments } from "./handleAttachments.ts";
import { handleInlineImages } from "./handleInlineImages.ts";
import { CancelManager } from "../lib/cancelManager.ts";

export const processArticle = async (
  token: string,
  brandUrl: string,
  article: ICreateArticle | IUpdateArticle,
  attachments: ILazyAttachment[],
  inlineImages: ILazyAttachment[],
  onNewProgressInfo: (title: string, type: ImportType) => void,
  onUpdateProgressInfo: (
    status: ImportProgressStatus,
    message: string,
    link: string,
    type?: ImportType,
  ) => void,
): Promise<IArticle> => {
  const cancelManager = CancelManager.getInstance();
  cancelManager.checkCancelled();

  let postedArticle: IArticle;
  // フォールバック時に id を除去した記事データに差し替える
  let effectiveArticle: ICreateArticle | IUpdateArticle = article;

  if ("id" in article) {
    onNewProgressInfo(article.title, "Update");
    try {
      postedArticle = await updateArticle(token, brandUrl, article);
    } catch (error) {
      // 記事が存在しない場合は新規作成にフォールバック
      const errorStr = String(error);
      if (errorStr.includes("リソースが存在しません")) {
        onUpdateProgressInfo("Upload", "記事が存在しないため新規作成します", "", "Create");
        // IUpdateArticle から id 等を除去して ICreateArticle に変換
        const createArticleData: ICreateArticle = {
          title: article.title,
          body: article.body,
          draft: article.draft,
          locale: article.locale,
          section_id: article.section_id,
          permission_group_id: article.permission_group_id,
          user_segment_id: article.user_segment_id,
          label_names: article.label_names,
          content_tag_ids: article.content_tag_ids,
          attachment_names: article.attachment_names,
        };
        effectiveArticle = createArticleData;
        postedArticle = await createArticle(token, brandUrl, createArticleData);
      } else {
        throw error;
      }
    }
  } else {
    onNewProgressInfo(article.title, "Create");
    postedArticle = await createArticle(token, brandUrl, article);
  }

  cancelManager.checkCancelled();
  onUpdateProgressInfo("Upload", "", postedArticle.html_url);

  if (effectiveArticle.attachment_names.length > 0) {
    await handleAttachments(
      token,
      brandUrl,
      postedArticle,
      effectiveArticle,
      attachments,
      onUpdateProgressInfo,
    );
  }

  cancelManager.checkCancelled();

  // インライン画像のアップロード・本文置換・孤立インライン添付の削除
  // UPDATE記事は孤立インライン添付クリーンアップのため常に実行
  const hasInlinePlaceholders = /\{\{inline_images\/[^}]+\}\}/.test(effectiveArticle.body);
  const isUpdate = "id" in article;
  if (hasInlinePlaceholders || isUpdate) {
    if (hasInlinePlaceholders) {
      onUpdateProgressInfo("Upload", "インライン画像をアップロード中...", postedArticle.html_url);
    }
    postedArticle = await handleInlineImages(
      token,
      brandUrl,
      postedArticle,
      effectiveArticle,
      inlineImages,
    );
  }

  cancelManager.checkCancelled();

  onUpdateProgressInfo("Success", "", postedArticle.html_url);

  return postedArticle;
};
